import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { CONFERENCIA_STATUS_LABELS, type ConferenciaStatusKey } from '@agencia/shared';
import { Alert, Button, Field } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { useApi } from '@/lib/useApi';

interface RespostaAvulsa {
  resultado: string;
  statusSite: string | null;
  voos: { numero: string; partida: string }[];
  mudou?: string[];
  sumiu?: string[];
  erro: string | null;
  evidencia: string | null;
  reserva: { id: string; localizador: string; cliente: { id: string; nome: string } } | null;
  conferencia: { status: ConferenciaStatusKey; gravada: boolean; motivo?: string } | null;
}

/** Companhias mostradas nos cartões. Só as ligadas no robô ficam clicáveis. */
const CATALOGO: { codigo: string; nome: string; rotuloCodigo: string; pedeOrigem: boolean }[] = [
  {
    codigo: 'LA',
    nome: 'LATAM',
    rotuloCodigo: 'Localizador ou número de compra',
    pedeOrigem: false,
  },
  { codigo: 'G3', nome: 'GOL', rotuloCodigo: 'Localizador', pedeOrigem: true },
  { codigo: 'AD', nome: 'Azul', rotuloCodigo: 'Localizador', pedeOrigem: false },
];

const CLASSE_RESULTADO: Record<string, string> = {
  Confirmado: 'pill--ok',
  Cancelado: 'pill--bad',
  Alterado: 'pill--warn',
  'Não encontrada': 'pill--bad',
};

/** Botão "Conferir reserva" + modal: o robô consulta uma reserva digitada, na hora. */
export function ConferirReserva({ onGravou }: { onGravou: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [aberto, setAberto] = useState(false);

  function abrir() {
    setAberto(true);
    dialogRef.current?.showModal();
  }
  function fechar() {
    dialogRef.current?.close();
  }

  return (
    <>
      <Button variant="secondary" onClick={abrir} className="conferir-btn">
        <IconeBilhete /> Conferir reserva
      </Button>
      <dialog
        ref={dialogRef}
        className="conferir"
        aria-labelledby="conferir-titulo"
        onClose={() => setAberto(false)}
        onClick={(e) => {
          // Clique no fundo escuro fecha (o conteúdo fica dentro de .conferir__corpo).
          if (e.target === e.currentTarget) fechar();
        }}
      >
        {aberto && <Conteudo onFechar={fechar} onGravou={onGravou} />}
      </dialog>
    </>
  );
}

function Conteudo({ onFechar, onGravou }: { onFechar: () => void; onGravou: () => void }) {
  const { data: ligadas } = useApi<{ cias: string[] }>('/crm/conferencia/avulsa/cias');
  const [cia, setCia] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [origem, setOrigem] = useState('');
  const [sobrenome, setSobrenome] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resposta, setResposta] = useState<RespostaAvulsa | null>(null);

  const ativas = new Set((ligadas?.cias ?? []).map((c) => (c === 'JJ' ? 'LA' : c)));
  const escolhida = CATALOGO.find((c) => c.codigo === cia);

  // Primeira companhia ligada já vem marcada.
  useEffect(() => {
    if (!cia && ligadas) setCia(CATALOGO.find((c) => ativas.has(c.codigo))?.codigo ?? null);
    // Só quando a lista de companhias chega.
  }, [ligadas]);

  async function conferir(e: FormEvent) {
    e.preventDefault();
    if (!escolhida) return;
    setErro(null);
    setResposta(null);
    setEnviando(true);
    try {
      const r = await api.post<RespostaAvulsa>('/crm/conferencia/avulsa', {
        companhia: escolhida.codigo,
        codigo,
        sobrenome,
        origem: escolhida.pedeOrigem ? origem : null,
      });
      setResposta(r);
      if (r.conferencia?.gravada) onGravou();
    } catch (err) {
      setErro(errorMessage(err));
    } finally {
      setEnviando(false);
    }
  }

  function novaConsulta() {
    setResposta(null);
    setErro(null);
    setCodigo('');
    setOrigem('');
    setSobrenome('');
  }

  return (
    <div className="conferir__corpo">
      <header className="conferir__topo">
        <h2 id="conferir-titulo">
          <IconeBilhete /> Conferir reserva
        </h2>
        <button type="button" className="conferir__fechar" aria-label="Fechar" onClick={onFechar}>
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {resposta ? (
        <Resultado
          r={resposta}
          cia={escolhida?.nome ?? ''}
          onNova={novaConsulta}
          onFechar={onFechar}
        />
      ) : (
        <form onSubmit={(e) => void conferir(e)} className="conferir__form">
          <fieldset className="conferir__cias">
            <legend>Selecione a companhia</legend>
            <div className="conferir__grade">
              {CATALOGO.map((c) => {
                const ativa = ativas.has(c.codigo);
                return (
                  <label
                    key={c.codigo}
                    className={`cia-card ${cia === c.codigo ? 'is-on' : ''} ${ativa ? '' : 'is-off'}`}
                  >
                    {!ativa && <span className="cia-card__breve">Em breve</span>}
                    <input
                      type="radio"
                      name="companhia"
                      value={c.codigo}
                      checked={cia === c.codigo}
                      disabled={!ativa || enviando}
                      onChange={() => setCia(c.codigo)}
                    />
                    <span className={`cia-card__marca cia-card__marca--${c.codigo.toLowerCase()}`}>{c.nome}</span>
                    <span className="cia-card__cod">código {c.codigo} no bilhete</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <Field
            label={escolhida?.rotuloCodigo ?? 'Localizador'}
            placeholder={
              escolhida?.codigo === 'LA' ? 'Ex.: ABC123 ou LA1234567ABCD' : 'Ex.: ABC123'
            }
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            required
            minLength={5}
            maxLength={24}
            autoComplete="off"
            autoFocus
            disabled={enviando}
          />
          {escolhida?.pedeOrigem && (
            <Field
              label="Origem"
              hint="Código do aeroporto do primeiro voo da reserva (ex.: GIG, SSA)."
              placeholder="Ex.: GIG"
              value={origem}
              onChange={(e) =>
                setOrigem(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z]/g, '')
                    .slice(0, 3),
                )
              }
              required
              pattern="[A-Z]{3}"
              autoComplete="off"
              disabled={enviando}
            />
          )}
          <Field
            label="Sobrenome"
            placeholder="Sobrenome"
            value={sobrenome}
            onChange={(e) => setSobrenome(e.target.value)}
            required
            minLength={2}
            autoComplete="off"
            disabled={enviando}
          />

          {erro && <Alert>{erro}</Alert>}
          {enviando && (
            <p className="muted small" role="status">
              Consultando o site da {escolhida?.nome}… leva de 15 a 40 segundos.
            </p>
          )}

          <div className="conferir__acoes">
            <Button
              type="submit"
              loading={enviando}
              disabled={!escolhida || !ativas.has(escolhida.codigo)}
            >
              <IconeLupa /> Conferir
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function Resultado({
  r,
  cia,
  onNova,
  onFechar,
}: {
  r: RespostaAvulsa;
  cia: string;
  onNova: () => void;
  onFechar: () => void;
}) {
  // Sem reserva no sistema, "Confirmado" do itinerário quer dizer só "achou a reserva no site".
  const rotulo = r.resultado === 'Confirmado' && !r.mudou ? 'Encontrada no site' : r.resultado;
  return (
    <div className="conferir__resultado" aria-live="polite">
      <div className="row between">
        <span className={`pill ${CLASSE_RESULTADO[r.resultado] ?? 'pill--warn'}`}>{rotulo}</span>
        <span className="muted small">Site da {cia}</span>
      </div>

      {r.statusSite && <p className="small">{r.statusSite}</p>}

      {r.voos.length > 0 && (
        <ul className="conferir__voos">
          {r.voos.map((v, i) => (
            <li key={i}>
              <strong>{v.partida}</strong> <span>{v.numero}</span>
            </li>
          ))}
        </ul>
      )}

      {r.mudou?.length ? <Alert kind="warning">Horário mudou: {r.mudou.join('; ')}.</Alert> : null}
      {r.sumiu?.length ? (
        <Alert kind="warning">
          Voo(s) do sistema que não aparecem no site: {r.sumiu.join(', ')}. Confira à mão.
        </Alert>
      ) : null}
      {r.erro && !r.sumiu?.length && <Alert kind="warning">{r.erro}</Alert>}

      {r.conferencia?.gravada ? (
        <Alert kind="success">
          Gravado na conferência como “{CONFERENCIA_STATUS_LABELS[r.conferencia.status]}”
          {r.reserva && (
            <>
              {' '}
              da reserva {r.reserva.localizador} de{' '}
              <Link to={`/clientes/${r.reserva.cliente.id}`}>{r.reserva.cliente.nome}</Link>
            </>
          )}
          .
        </Alert>
      ) : r.conferencia?.motivo ? (
        <p className="muted small">{r.conferencia.motivo}</p>
      ) : null}

      {r.evidencia && (
        <details>
          <summary className="small">Ver captura de tela do site</summary>
          <img
            src={r.evidencia}
            alt={`Página do site da ${cia} no momento da consulta`}
            className="conferir__captura"
          />
        </details>
      )}

      <div className="conferir__acoes">
        <Button variant="secondary" onClick={onFechar}>
          Fechar
        </Button>
        <Button onClick={onNova}>Conferir outra</Button>
      </div>
    </div>
  );
}

function IconeBilhete() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="icone">
      <path
        d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6V7z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M10 8v1M10 11.5v1M10 15v1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconeLupa() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="icone">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
